import { useAuth } from "@/auth/auth";
import {
  useBazelBuildMeta,
  useBazelBuildMetas,
  useBazelBuildOutput,
  useBazelBuilds,
  type BazelBuild,
  type BuildLogLine,
} from "@/client/director/buildLogs.tsx";
import { orgRequestOptions } from "@/client/director/org.ts";
import { BazelInsights } from "@/components/BazelInsights";
import {
  BazelOverview,
  ExecStatusBadge,
  LiveTimer,
  decodeSSEData,
} from "@/components/BuildLogs";
import { CopyButton, CopyableCommand } from "@/components/copy";
import { AppShell, EmptyState, PageHeader } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { XtermLogPanel, joinChunks } from "@/components/XtermLogPanel.tsx";
import {
  barToneClass,
  cacheLabel,
  cacheStats,
  hasStatus,
  isTerminalStatus,
  runnerTone,
  targetOf,
  type BarTone,
} from "@/lib/bazel";
import { INSIGHTS_WINDOW_DAYS, inWindow } from "@/lib/bazel-insights";
import { formatDurationMs, formatRelativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ChevronRight, Download, Hammer } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

// The list shows the newest builds; older records stay in storage.
const MAX_BUILDS = 50;

// Cache hits lead the strip, so the green share lines up from row to row.
const toneOrder: Record<BarTone, number> = {
  hit: 0,
  remote: 1,
  local: 2,
  internal: 3,
  accent: 4,
};

// parseSSE splits complete Server-Sent Events frames off the buffer and
// returns what is left for the next read.
function parseSSE(
  buffer: string,
  dispatch: (event: string, data: string) => void,
): string {
  const text = buffer.replace(/\r\n/g, "\n");
  let start = 0;
  for (
    let end = text.indexOf("\n\n");
    end >= 0;
    end = text.indexOf("\n\n", start)
  ) {
    let event = "message";
    const data: string[] = [];
    for (const line of text.slice(start, end).split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:"))
        data.push(line.slice(5).replace(/^ /, ""));
    }
    dispatch(event, data.join("\n"));
    start = end + 2;
  }
  return text.slice(start);
}

// useLiveBuildOutput streams a running build's console from the director.
// EventSource cannot send the console's session headers, so this reads the
// stream with fetch, with the region client's auth and the org pinned per
// request. It stops at the "end" event; the list's next poll then swaps the
// stream for the build's record.
function useLiveBuildOutput(
  buildId: string | undefined,
  region: string | undefined,
): BuildLogLine[] {
  const auth = useAuth();
  const [lines, setLines] = useState<BuildLogLine[]>([]);

  useEffect(() => {
    setLines([]);
    if (!buildId) return;
    const abort = new AbortController();
    const pending: BuildLogLine[] = [];
    let frame: number | undefined;
    // Batch per animation frame, so a fast build does not re-render per line.
    const flush = () => {
      frame = undefined;
      const batch = pending.splice(0);
      setLines((current) => current.concat(batch));
    };
    const dispatch = (event: string, data: string) => {
      if (event === "end") abort.abort();
      if (event !== "stdout" && event !== "stderr") return;
      pending.push({ type: event, data: decodeSSEData(data) });
      frame ??= requestAnimationFrame(flush);
    };
    const url = new URL(
      `${auth.regionClient.baseURL.replace(/\/+$/, "")}/v1/bazel_builds/${encodeURIComponent(buildId)}/events`,
    );
    url.searchParams.set("encoding", "json");
    if (region) url.searchParams.set("region", region);
    // stream reads until the stream closes. It reports false when the build is
    // no longer running: a finished build answers 404 and its record takes over.
    const stream = async (): Promise<boolean> => {
      const res = await fetch(url, {
        signal: abort.signal,
        ...(auth.token ? {} : { credentials: "include" as const }),
        headers: {
          ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}),
          "X-Limrun-Client": "console",
          ...orgRequestOptions(auth.organizationTid)?.headers,
        },
      });
      if (res.status === 404) return false;
      if (!res.ok || !res.body) return true;
      const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) return true;
        buffer = parseSSE(buffer + value, dispatch);
      }
    };
    // The plane replays a build's stream from its start, so a dropped
    // connection reconnects and redraws instead of freezing the pane.
    (async () => {
      while (!abort.signal.aborted) {
        const again = await stream().catch(() => true);
        if (!again || abort.signal.aborted) return;
        await new Promise((resolve) => setTimeout(resolve, 2_000));
        if (abort.signal.aborted) return;
        pending.length = 0;
        setLines([]);
      }
    })();
    return () => {
      abort.abort();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [buildId, region, auth.regionClient, auth.token, auth.organizationTid]);

  return lines;
}

// CacheStrip shows at a glance where a build's actions ran: served from the
// remote cache, executed on a worker, or handled inside Bazel.
function CacheStrip({ build }: { build: BazelBuild }) {
  const actions = useBazelBuildMeta(build)?.actions;
  if (!actions?.runners?.length || actions.total === 0) {
    return <span className="h-1.5 flex-1" />;
  }
  const cache = cacheStats(actions, build.status);
  const runners = actions.runners
    .slice()
    .sort(
      (a, b) => toneOrder[runnerTone(a.name)] - toneOrder[runnerTone(b.name)],
    );
  const label = cacheLabel(cache);
  return (
    <span className="flex flex-1 items-center gap-2" title={label}>
      <span
        role="img"
        aria-label={label}
        className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
      >
        {runners.map((r) => (
          <span
            key={r.name}
            className={barToneClass[runnerTone(r.name)]}
            style={{ width: `${(r.count / actions.total) * 100}%` }}
          />
        ))}
      </span>
      <span className="w-9 shrink-0 text-right tabular-nums">
        {cache.kind === "rate"
          ? `${cache.ratePct}%`
          : cache.kind === "upToDate"
            ? "cached"
            : "-"}
      </span>
    </span>
  );
}

// Rows re-render only when their build changes, not on every list poll.
const BuildRow = memo(
  function BuildRow({
    build,
    selected,
    onSelect,
  }: {
    build: BazelBuild;
    selected: boolean;
    onSelect: (id: string) => void;
  }) {
    const running = hasStatus(build.status, "RUNNING");
    return (
      <button
        type="button"
        onClick={() => onSelect(build.id)}
        aria-current={selected ? "true" : undefined}
        className={cn(
          "flex w-full flex-col gap-1.5 px-3 py-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          selected && "bg-muted",
          running && "border-l-2 border-l-blue-400",
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <ExecStatusBadge status={build.status} />
          {build.startedAt && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {build.region ? `${build.region}, ` : ""}
              {formatRelativeTime(build.startedAt)}
            </span>
          )}
        </div>
        <span className="truncate font-mono text-xs" title={targetOf(build)}>
          {targetOf(build)}
        </span>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {running ? (
            <>
              <span className="flex-1">Building on Limrun</span>
              {build.startedAt && <LiveTimer startedAt={build.startedAt} />}
            </>
          ) : (
            <>
              <CacheStrip build={build} />
              {build.durationMs != null && (
                <span className="w-14 shrink-0 text-right tabular-nums">
                  {formatDurationMs(build.durationMs)}
                </span>
              )}
            </>
          )}
        </div>
      </button>
    );
  },
  (a, b) =>
    a.selected === b.selected &&
    a.onSelect === b.onSelect &&
    a.build.id === b.build.id &&
    a.build.status === b.build.status &&
    a.build.durationMs === b.build.durationMs &&
    a.build.startedAt === b.build.startedAt &&
    targetOf(a.build) === targetOf(b.build) &&
    !!a.build.metadataDownloadUrl === !!b.build.metadataDownloadUrl,
);

function GetStarted() {
  return (
    <div className="flex flex-col items-center pb-14">
      <EmptyState
        icon={<Hammer />}
        title="No Bazel builds yet"
        description="Builds that run on Limrun show up here, with how much came from your organization's shared cache."
      />
      <ol className="-mt-6 flex w-full max-w-xl flex-col gap-4">
        {[
          {
            title: "Point your workspace at Limrun",
            detail: "Run it in the workspace root, next to MODULE.bazel.",
            command: "lim bazel setup",
          },
          {
            title: "Build with the limrun config",
            detail:
              "Apple actions run on Limrun's Macs, Android actions on its Linux workers.",
            command: "bazel build --config=limrun //your:target",
          },
        ].map((step, i) => (
          <li key={step.command} className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium tabular-nums text-muted-foreground">
              {i + 1}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{step.title}</span>
                <span className="text-xs text-muted-foreground">
                  {step.detail}
                </span>
              </div>
              <CopyableCommand
                command={step.command}
                label={`Copy ${step.command}`}
              />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function BuildDetail({ build }: { build: BazelBuild }) {
  const running = hasStatus(build.status, "RUNNING");
  // A build stays live until its record is saved: one that just finished
  // keeps the output streamed so far instead of dropping it.
  const live = running || !build.downloadUrl;
  const meta = useBazelBuildMeta(build);
  const output = useBazelBuildOutput(build);
  const liveLines = useLiveBuildOutput(
    live ? build.id : undefined,
    build.region,
  );
  const lines = live ? liveLines : output.lines;
  const text = useMemo(
    () => (lines === "loading" ? "" : joinChunks(lines, false, true)),
    [lines],
  );
  const cache = meta?.actions
    ? cacheStats(meta.actions, build.status)
    : undefined;

  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${build.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-background">
      <div className="flex flex-col gap-1 border-b px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <h2
            className="truncate font-mono text-sm font-semibold"
            title={targetOf(build)}
          >
            {targetOf(build)}
          </h2>
          <ExecStatusBadge status={build.status} />
          {lines !== "loading" && (
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <CopyButton value={text} label="Copy to clipboard" />
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={download}
                      aria-label="Download log"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Download log</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {build.startedAt && (
            <span className="shrink-0">
              {new Date(build.startedAt).toLocaleString()}
            </span>
          )}
          {build.region && <span className="shrink-0">{build.region}</span>}
          {running && build.startedAt ? (
            <LiveTimer startedAt={build.startedAt} />
          ) : (
            build.durationMs != null && (
              <span className="shrink-0">
                {formatDurationMs(build.durationMs)}
              </span>
            )
          )}
          {cache && meta?.actions && (
            <span className="shrink-0">
              {meta.actions.total} action{meta.actions.total === 1 ? "" : "s"},{" "}
              {cacheLabel(cache)}
            </span>
          )}
          {build.error && (
            <span className="truncate text-red-600" title={build.error}>
              {build.error}
            </span>
          )}
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {meta && (
          <Collapsible
            defaultOpen
            className="flex max-h-[45%] shrink-0 flex-col border-b"
          >
            <CollapsibleTrigger className="group flex shrink-0 items-center gap-1 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
              <ChevronRight className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-90" />
              Overview
            </CollapsibleTrigger>
            <CollapsibleContent className="min-h-0 overflow-y-auto">
              <BazelOverview meta={meta} />
            </CollapsibleContent>
          </Collapsible>
        )}
        {output.error && !live ? (
          <p className="p-4 text-sm text-red-600">
            Could not load the build output: {output.error.message}
          </p>
        ) : live && !running && liveLines.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            The build finished. Its output appears here once it is saved.
          </p>
        ) : lines === "loading" ? (
          <p className="p-4 text-sm text-muted-foreground">Loading...</p>
        ) : (
          <XtermLogPanel
            key={`${live ? "live" : "done"}-${build.id}`}
            lines={lines}
            finished={isTerminalStatus(build.status)}
          />
        )}
      </div>
    </div>
  );
}

export function BazelBuildsPage() {
  const { organizationTid } = useAuth();
  const [tab, setTab] = useState("overview");
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const { data, error } = useBazelBuilds({ organizationTid });
  const all = data === "loading" ? [] : data;
  const builds = all.slice(0, MAX_BUILDS);
  // A chart bar can open a build older than the list shows.
  const selected = all.find((b) => b.id === selectedId) ?? builds[0];
  // Insights cover the whole window; only the list stops at MAX_BUILDS.
  const recent = inWindow(all, Date.now());
  const metas = useBazelBuildMetas(recent);
  const openBuild = useCallback((id: string) => {
    setSelectedId(id);
    setTab("builds");
  }, []);

  // A failed poll keeps the last good list; only a first load shows an error.
  const body =
    data === "loading" ? (
      error ? (
        <p className="text-sm text-red-600">
          Could not load builds: {error.message}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">Loading...</p>
      )
    ) : builds.length === 0 ? (
      <GetStarted />
    ) : (
      <Tabs
        value={tab}
        onValueChange={setTab}
        className="flex min-h-0 flex-1 flex-col gap-4"
      >
        <TabsList className="self-start">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="builds">Builds</TabsTrigger>
        </TabsList>
        <TabsContent
          value="overview"
          className="mt-0 min-h-0 flex-1 overflow-y-auto"
        >
          {recent.length === 0 ? (
            <EmptyState
              icon={<Hammer />}
              title={`No builds in the last ${INSIGHTS_WINDOW_DAYS} days`}
              description="Insights cover recent builds. Your organization's earlier builds are on the Builds tab."
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTab("builds")}
              >
                View builds
              </Button>
            </EmptyState>
          ) : (
            <BazelInsights
              builds={recent}
              metas={metas}
              onSelectBuild={openBuild}
            />
          )}
        </TabsContent>
        <TabsContent
          value="builds"
          className="mt-0 flex min-h-0 flex-1 flex-col gap-4 overflow-hidden data-[state=inactive]:hidden lg:flex-row"
        >
          <section
            aria-label="Builds"
            className="flex h-56 min-h-0 w-full shrink-0 flex-col overflow-hidden rounded-lg border bg-background lg:h-auto lg:w-80"
          >
            <div className="flex shrink-0 items-center justify-between border-b px-3 py-2">
              <h2 className="text-sm font-semibold">Builds</h2>
              <span className="text-xs text-muted-foreground">
                {builds.length}
              </span>
            </div>
            <ScrollArea className="min-h-0 flex-1">
              {builds.map((build, i) => (
                <div key={build.id}>
                  {i > 0 && <div className="border-t" />}
                  <BuildRow
                    build={build}
                    selected={build.id === selected?.id}
                    onSelect={setSelectedId}
                  />
                </div>
              ))}
            </ScrollArea>
          </section>
          {selected && <BuildDetail key={selected.id} build={selected} />}
        </TabsContent>
      </Tabs>
    );

  return (
    <AppShell title="Bazel" viewport>
      <PageHeader
        title="Bazel"
        description="Builds your organization ran on Limrun's build plane."
      />
      {body}
    </AppShell>
  );
}
